import { Button } from '@ria-client/tw-shadcn/components/button';
import Image from 'next/image';
import type { CSSProperties } from 'react';
import { COLUMNS_COLORS } from '../lib/colors';
import { ILLUSTRATIONS } from '../lib/illustrations';
import type { NodeProps } from '../types';
import { ContentLink } from './lib/content-link';
import { MarketplaceEntityCTA } from './lib/marketplace-entity-cta';

const MAX_ITEMS = 4;

export interface SMBIllustrationCardListItem {
  illustration: string;
  headline?: string;
  subtext?: string;
}

export interface SMBIllustrationCardListProps extends Record<string, unknown> {
  title: string;
  subtitle?: string;
  backgroundColor?: string;
  hasBorder?: boolean;
  items: SMBIllustrationCardListItem[];
  ctaHref?: string;
  ctaText?: string;
  marketplaceEntityId?: string;
  redirectToPartner?: boolean;
}

export function SMBIllustrationCardList({ props }: NodeProps<SMBIllustrationCardListProps>) {
  const backgroundColor =
    COLUMNS_COLORS[props.backgroundColor as keyof typeof COLUMNS_COLORS] ?? 'transparent';

  return (
    <section
      className={`flex flex-col items-center gap-6 p-6 text-center${props.hasBorder ? ' border border-border' : ''}`}
      style={{ backgroundColor } as CSSProperties}
    >
      <h2 className="font-display text-[32px] font-normal md:text-4xl">{props.title}</h2>
      {props.subtitle ? <p className="typography-text-bold">{props.subtitle}</p> : null}
      <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start">
        {props.items.slice(0, MAX_ITEMS).map(({ illustration, headline, subtext }) => {
          const asset = ILLUSTRATIONS[illustration];
          if (!asset) return null;

          return (
            <div className="flex flex-col items-center gap-4" key={`${illustration}-${headline}`}>
              <Image
                alt={asset.alt}
                className="size-[90px] md:size-[110px]"
                height={110}
                src={asset.url}
                width={110}
              />
              {headline ? <p className="typography-text-bold">{headline}</p> : null}
              {subtext ? <p className="typography-text">{subtext}</p> : null}
            </div>
          );
        })}
      </div>
      {props.redirectToPartner && props.marketplaceEntityId ? (
        <MarketplaceEntityCTA
          marketplaceEntityId={props.marketplaceEntityId}
          text={props.ctaText}
        />
      ) : null}
      {!props.redirectToPartner && props.ctaHref && props.ctaText ? (
        <Button asChild>
          <ContentLink href={props.ctaHref}>{props.ctaText}</ContentLink>
        </Button>
      ) : null}
    </section>
  );
}
